import { useState, useEffect } from "react";
import type { ProductSource } from "@/types";
import {
  createProductSourceRequestSchema,
  updateProductSourceRequestSchema,
} from "@/validation";
import { zodToFieldErrors, firstFieldErrors } from "@trillion/validation";
import { validateTrackingConfig } from "@/lib/json-utils";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import { Switch } from "@trillion/ui/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@trillion/ui/components/ui/card";
import { Loader2 } from "lucide-react";

interface NetworkFormData {
  code: string;
  name: string;
  tracking_config: string;
  active: boolean;
}

interface NetworkFormProps {
  initialData?: ProductSource;
  onSubmit: (data: Partial<ProductSource>) => Promise<void>;
  isLoading: boolean;
}

function toFormData(network?: ProductSource): NetworkFormData {
  if (!network) {
    return { code: "", name: "", tracking_config: "", active: true };
  }
  return {
    code: network.code,
    name: network.name,
    tracking_config: network.tracking_config ?? "",
    active: network.active,
  };
}

export function NetworkForm({ initialData, onSubmit, isLoading }: NetworkFormProps) {
  const [form, setForm] = useState<NetworkFormData>(() => toFormData(initialData));
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setForm(toFormData(initialData));
    }
  }, [initialData]);

  function updateField(field: keyof NetworkFormData, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // tracking_config: epic contract {"sub_id_param": "<query-param name>"} — validated client-side;
    // empty input serializes as null (the schema allows null; the server stores freeform jsonb).
    const configResult = validateTrackingConfig(form.tracking_config);
    if (!configResult.ok) {
      setErrors((prev) => ({ ...prev, tracking_config: configResult.error }));
      return;
    }

    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      tracking_config: configResult.normalized,
      active: form.active,
    };
    const schema = initialData ? updateProductSourceRequestSchema : createProductSourceRequestSchema;
    const result = schema.safeParse(payload);
    if (!result.success) {
      setErrors(firstFieldErrors(zodToFieldErrors(result.error)));
      return;
    }

    await onSubmit(payload);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{initialData ? "Edit Network" : "New Network"}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                value={form.code}
                onChange={(e) => updateField("code", e.target.value)}
                placeholder="awin"
              />
              {errors.code && <p className="text-sm text-destructive">{errors.code}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="Awin"
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="tracking_config">Tracking Config (JSON)</Label>
            <textarea
              id="tracking_config"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={form.tracking_config}
              onChange={(e) => updateField("tracking_config", e.target.value)}
              placeholder='{"sub_id_param": "clickref"}'
            />
            <p className="text-xs text-muted-foreground">
              JSON object with a required <code>sub_id_param</code> — the network&apos;s sub-ID query
              parameter name (e.g. awin <code>clickref</code>, cj <code>sid</code>). Leave empty for
              none.
            </p>
            {errors.tracking_config && (
              <p className="text-sm text-destructive">{errors.tracking_config}</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="active"
              checked={form.active}
              onCheckedChange={(checked) => updateField("active", checked)}
            />
            <Label htmlFor="active">Active</Label>
          </div>

          <div className="flex gap-3">
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
              {initialData ? "Update Network" : "Create Network"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
